/* eslint-disable max-lines-per-function */
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { getGeneralInfo } from "@/store/account/actions";
import { EditorWebsiteModeOptions, EditorWebsiteModeOutputFormat, EditorWebsiteModeType } from "@dev/template-editor";
import { usePlanHaveRecaptchaFeature } from "@/services/hooks/usePlanHaveRecaptchaFeature";
import { useFormMergeFields } from "@/components/CheckoutCreator/hooks/useFormMergeFields";
import { RecaptchaLockedInfo } from '@/components/FormCreator/components/RecaptchaLockedInfo';
import { LockIcon } from '@/services/icons/LockedIcon';
import { useTranslation } from 'react-i18next';
import { OpenUpsellingOverlay } from '@/store/templateEditor/actions';
import { getCheckoutCreatorStore } from '@/store/checkoutCreator/actions';
import { AvailableFeaturesUpselling } from '@dev/ee-modules';
import { websiteStyles } from "../tools/getWebsiteStyles";
import { MultiTemplateId, TEMPLATE_EDITOR_NO_BLOCK_LIMIT, WEBSITE_BUY_URL, WEBSITE_CHECKOUT_URL } from "../constants";
import { FormMultiTerms } from '../components/FormMultiTerms';
import { useMultiSignUpSettings } from './useMultiSignUpSettings';

type MultiTemplatesSettings = Record<MultiTemplateId, EditorWebsiteModeOptions>;

interface MultiTemplatesWebsiteOptionsProps {
    isGalleryCreator: boolean;
    selectedBlockIdRef: React.MutableRefObject<string>;
}

export const useMultiTemplatesWebsiteOptions = ({ isGalleryCreator, selectedBlockIdRef }: MultiTemplatesWebsiteOptionsProps): MultiTemplatesSettings  => {
    const dispatch = useDispatch();
    const isUser = !isGalleryCreator;
    const { PublicAccountID } = useSelector(getGeneralInfo);
    const { fields } = useFormMergeFields({isGalleryCreator});
    const { template } = useSelector(getCheckoutCreatorStore);
    const { fields: fieldsPayment } = useFormMergeFields({isGalleryCreator, includePaymentFields: true});
    const isPlanHaveRecaptchaFeature = usePlanHaveRecaptchaFeature();
    const { t } = useTranslation();
    const multiSignUpSettings = useMultiSignUpSettings({ selectedBlockIdRef });

    const onRecaptchaUpselling = (): void => {
        dispatch(OpenUpsellingOverlay(AvailableFeaturesUpselling.Recaptcha));
    };

    return useMemo(() => {

    
    const websiteModeOptions: EditorWebsiteModeOptions = {
        mode: EditorWebsiteModeType.FullWebsite,
        output: {
            format: EditorWebsiteModeOutputFormat.FullTemplate,
        },
        styles: websiteStyles,
        signUpSettings: {
            actionUrl: `${APIPATH}/v4/contact/form`,
            target: '_self',
            hiddenInputs: {
                publicaccountid: PublicAccountID,
            },
            fields,
            lists: [],
            selectedLists: [],
        },
        components: {
            recaptchaLockedInfoComponent: isPlanHaveRecaptchaFeature?.ViewAntiSpamUpsellingBanner ? (
                <RecaptchaLockedInfo
                    button={{
                        text: t('upgradetounlock'),
                        startIcon: <LockIcon width={20} height={20} />,
                        onClick: onRecaptchaUpselling,
                    }}
                />
            ) : null,
        },
        isRecaptchaHidden: !isPlanHaveRecaptchaFeature?.ViewSubscriptionAntiSpamFeature,
        isTermsHidden: true,
        rows: {},
        content: {},
        blocks: {},
        multiSignUpEditMessage: template?.multiSignUpEditMessage,
    };

    return ({
        [MultiTemplateId.PRODUCT]: {
            ...websiteModeOptions,
            rows: { hide: false },
            blocks: {
                paragraph: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    allowTransformation: true,
                },
                heading: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    allowTransformation: true,
                },
                image: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
                divider: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
                social: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
                list: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
                form: {
                    minOccurrences: 0,
                    maxOccurrences: 0,
                },
                formMulti: {
                    minOccurrences: 0,
                    maxOccurrences: 0,
                },
                button: {
                    isBelowMinOccurrencesBlocked: true,
                    minOccurrences: 1,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    linkTo: {
                        description: t('checkoutbutton_description'),
                        staticUrl: WEBSITE_CHECKOUT_URL
                    }
                },
                spacer: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
                video: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
            }
        },
        [MultiTemplateId.PAYMENT]: {
            ...websiteModeOptions,
            components: {
                termsComponent: <FormMultiTerms />
            },
            signUpSettings: {
                ...websiteModeOptions.signUpSettings,
                fields: fieldsPayment,
                actionUrl: WEBSITE_BUY_URL,
                hiddenInputs: {},
            },
            multiSignUpSettings: {
                default: {
                    actionUrl: WEBSITE_BUY_URL,
                    hiddenInputs: {},
                }
            },
            rows: {hide: isUser, disableSelection: isUser },
            content: { hide: isUser },
            styling: { 
                hideTextSection: isUser,
                hideButtonsSection: isUser,
                hideBrandColorsSection: isUser,
            },
            disableBlockActions: {
                remove: isUser,
                duplicate: isUser,
                move: isUser
            },
            isTermsHidden: false,
            isCreateNewFormFieldHidden: true,
            isRecaptchaHidden: true,
            blocks: {
                paragraph: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    allowTransformation: true,
                    // disabledOptions: {
                    //     isContentEditDisabled: isUser,
                    // }
                },
                heading: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    allowTransformation: true,
                    disabledOptions: {
                        isContentEditDisabled: isUser,
                    }
                },
                image: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    linkTo: {
                        isHidden: true,
                    }
                },
                divider: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
                social: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
                list: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
                formMulti: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
                button: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
                spacer: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
                video: {
                    minOccurrences: 0,
                    maxOccurrences: 0,
                },
            }
        },
        [MultiTemplateId.CONFIRMATION]: {
            ...websiteModeOptions,
            multiSignUpSettings,
            rows: { hide: false },
            blocks: {
                paragraph: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    allowTransformation: true,
                },
                heading: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    allowTransformation: true,
                },
                image: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
                divider: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
                social: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
                list: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
                formMulti: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
                button: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
                spacer: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
                video: {
                    minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                    maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                },
            }
        }
    });
    }, [PublicAccountID, isGalleryCreator, fields]);
};

